// src/pages/admin/AdminDashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  BadgeDollarSign,
  Wallet, ReceiptText, CircleDollarSign, ArrowDownToLine,
  Boxes,
  CalendarDays,
  ChartNoAxesCombined,
  Eye,
  Menu,
  MousePointerClick,
  Package,
  SearchCheck,
  RefreshCcw,
  ShoppingCart,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";

import {
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import Sidebar from "../../components/Sidebar";
import { API_BASE_URL } from "../../config/api";

const initialStats = {
  products: 0,
  productTypes: 0,
  parts: 0,
  orders: 0,
  repairs: 0,
  repairsTotal: 0,
  visitors: 0,
  webVisitors: 0,
  webPageViews: 0,
  users: 0,

  topSoldProduct: null,
  topClickedProduct: null,
  topSearchedProduct: null,
  topSearchTerm: null,

  sales: 0,
  salesTotal: 0,
  soldQuantity: 0,

  salesByUser: [],
  repairsByUser: [],
  salesTimeline: [],
  repairsTimeline: [],
  visitorsTimeline: [],
  netProfit: null,
  cashResult: null,
  financialSummary: null,
  intelligentBalance: null,
};

const formatMoney = (value) =>
  new Intl.NumberFormat("sq-AL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const normalizeDateValue = (value) => {
  if (!value) return "";

  // MySQL/JSON mund të kthejë "2026-09-11T00:00:00.000Z"
  // ose direkt "2026-09-11".
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
};

const formatDateDMY = (value) => {
  const normalized = normalizeDateValue(value);
  if (!normalized) return "";

  const [year, month, day] = normalized.split("-");
  return `${day}/${month}/${year}`;
};

const isDateInsideRange = (value, startDate, endDate) => {
  const normalized = normalizeDateValue(value);
  if (!normalized) return false;

  if (startDate && normalized < startDate) return false;
  if (endDate && normalized > endDate) return false;

  return true;
};

const normalizePerson = (value) => String(value || "")
  .normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("sq-AL");

const getPeriodRange = (period, reference) => {
  const year = reference.slice(0, 4);
  const month = reference.slice(5, 7);
  if (period === "day") return [reference, reference];
  if (period === "month") {
    const last = new Date(Number(year), Number(month), 0).getDate();
    return [`${year}-${month}-01`, `${year}-${month}-${String(last).padStart(2, "0")}`];
  }
  return [`${year}-01-01`, `${year}-12-31`];
};

const AdminDashboard = () => {
  const navigate = useNavigate();

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const today = (() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  })();

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  const [appliedStartDate, setAppliedStartDate] = useState(today);
  const [appliedEndDate, setAppliedEndDate] = useState(today);

  const [stats, setStats] = useState(initialStats);
  const [balancePeriod, setBalancePeriod] = useState("day");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDateRange, setShowDateRange] = useState(false);

  const handleSearch = () => {
    setError("");

    if (!startDate || !endDate) {
      setError("Zgjidh datën Nga dhe datën Deri.");
      return;
    }

    if (startDate > endDate) {
      setError("Data Nga nuk mund të jetë pas datës Deri.");
      return;
    }

    setBalancePeriod("custom");
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setRefreshKey((value) => value + 1);
    setShowDateRange(false);
  };

  /* =====================================================
     FETCH STATS
  ===================================================== */

  useEffect(() => {
    const controller = new AbortController();

    const fetchStats = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/stats?startDate=${encodeURIComponent(
            appliedStartDate
          )}&endDate=${encodeURIComponent(appliedEndDate)}`,
          { signal: controller.signal }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.sqlMessage ||
            data?.details ||
            data?.error ||
            "Nuk u ngarkuan statistikat."
          );
        }

        setStats({
          products: Number(data.products || 0),
          productTypes: Number(data.productTypes || 0),
          parts: Number(data.parts || 0),
          orders: Number(data.orders || 0),
          repairs: Number(data.repairs || 0),
          repairsTotal: Number(data.repairsTotal || 0),
          visitors: Number(data.visitors || 0),
          webVisitors: Number(data.webVisitors || 0),
          webPageViews: Number(data.webPageViews || 0),
          users: Number(data.users || 0),

          topSoldProduct:
            data.topSoldProduct || null,

          topClickedProduct:
            data.topClickedProduct || null,

          topSearchedProduct:
            data.topSearchedProduct || null,

          topSearchTerm:
            data.topSearchTerm || null,

          sales: Number(data.sales || 0),
          salesTotal: Number(data.salesTotal || 0),
          soldQuantity: Number(data.soldQuantity || 0),

          salesByUser: Array.isArray(data.salesByUser)
            ? data.salesByUser
            : [],

          repairsByUser: Array.isArray(data.repairsByUser)
            ? data.repairsByUser
            : [],

          salesTimeline: Array.isArray(data.salesTimeline)
            ? data.salesTimeline
            : [],

          repairsTimeline: Array.isArray(data.repairsTimeline)
            ? data.repairsTimeline
            : [],

          visitorsTimeline: Array.isArray(data.visitorsTimeline)
            ? data.visitorsTimeline
            : [],
          financialSummary: data.financialSummary || null,
          intelligentBalance: data.intelligentBalance || data.financialSummary?.intelligentBalance || null,
          cashResult: data.cashResult == null || !Number.isFinite(Number(data.cashResult)) ? null : Number(data.cashResult),
          netProfit: data.netProfit == null || !Number.isFinite(Number(data.netProfit))
            ? null
            : Number(data.netProfit),
        });
      } catch (fetchError) {
        if (fetchError.name !== "AbortError") {
          console.error("Dashboard error:", fetchError);
          setError(
            fetchError.message ||
            "Nuk u ngarkuan statistikat."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    return () => controller.abort();
  }, [appliedStartDate, appliedEndDate, refreshKey]);

  const applyBalancePeriod = (period) => {
    const [from, to] = getPeriodRange(period, today);
    setBalancePeriod(period);
    setStartDate(from);
    setEndDate(to);
    setAppliedStartDate(from);
    setAppliedEndDate(to);
    setShowDateRange(false);
  };

  // Use the same backend snapshot for revenue, expenses and profit.
  const financial = useMemo(() => {
    const summary = stats.financialSummary;
    const expenseTotals = summary?.expenses;
    return {
      total: Number(expenseTotals?.total || 0),
      personalTotal: Number(expenseTotals?.personal || 0),
      operatingTotal: Number(expenseTotals?.operating || 0),
      supplierPayments: Number(expenseTotals?.supplierPayments || 0),
      payrollPayments: Number(expenseTotals?.payrollPayments || 0),
      personalFromExpenses: Number(expenseTotals?.personalFromExpenses || 0),
      devicePurchases: Number(expenseTotals?.devicePurchases || 0),
      personal: (summary?.withdrawalsByUser || []).map((person) => ({
        id: person.user_id ?? person.name,
        name: person.name,
        amount: Number(person.amount || 0),
        count: Number(person.count || 0),
      })),
      categories: (summary?.expensesByCategory || []).map((category) => ({
        name: category.category,
        amount: Number(category.amount || 0),
      })),
    };
  }, [stats.financialSummary]);

  // Keep accounting profit separate from recorded cash movements.
  const smartBalance = stats.intelligentBalance;
  const smartSales = smartBalance?.sales;
  const smartRepairs = smartBalance?.repairs;
  const smartWithdrawals = smartBalance?.withdrawals;
  const smartTotals = smartBalance?.balance;
  const smartPeople = Array.isArray(smartWithdrawals?.byPerson) ? smartWithdrawals.byPerson : [];
  const normalizeWithdrawalName = (value) => String(value || "").trim().toLocaleLowerCase("sq-AL");
  const withdrawalAliases = {
    Leard: ["leard", "leardi"], Bekim: ["bekim", "bekimi"],
    Marin: ["marin", "marini"], Nolti: ["nolti", "nolt"],
  };
  const withdrawalFor = (name) => {
    const aliases = withdrawalAliases[name];
    const fromSmart = smartPeople.find((item) => aliases.includes(normalizeWithdrawalName(item.name)));
    if (smartBalance) return fromSmart ? { amount: Number(fromSmart.total || 0), count: Number(fromSmart.count || 0), verified: true } : { amount: 0, count: 0, verified: true };
    const legacy = financial.personal.filter((item) => aliases.includes(normalizeWithdrawalName(item.name)));
    return legacy.length ? {
      amount: legacy.reduce((sum, item) => sum + item.amount, 0),
      count: legacy.reduce((sum, item) => sum + item.count, 0), verified: false,
    } : null;
  };
  // Derive remaining profit from verified component values if the API has not
  // yet deployed the remainingProfit field. Never coerce missing values to zero.
  const supplierDeduction = smartTotals?.supplierPayments ?? smartTotals?.supplierCashOut;
  const deviceDeduction = smartTotals?.devicePurchases ?? smartTotals?.devicePurchaseCashOut;
  const remainingProfit = smartTotals?.grossProfit != null && smartTotals?.operatingExpenses != null && supplierDeduction != null && deviceDeduction != null && smartWithdrawals?.total != null
    ? Number(smartTotals.grossProfit) - Number(smartTotals.operatingExpenses) - Number(supplierDeduction) - Number(deviceDeduction) - Number(smartWithdrawals.total)
    : null;
  const smartAlerts = Array.isArray(smartBalance?.alerts) ? smartBalance.alerts : [];
  const smartMoney = (value) => value == null || !Number.isFinite(Number(value))
    ? "—" : `${formatMoney(value)} ALL`;

  const expensesReady = Boolean(stats.financialSummary?.expensesAvailable);
  const expensesError = stats.financialSummary && !expensesReady
    ? "Tabela e shpenzimeve nuk ka kolonat e nevojshme."
    : "";

  /* =====================================================
     CALCULATED VALUES
  ===================================================== */

  const totalRevenue =
    Number(stats.salesTotal || 0) +
    Number(stats.repairsTotal || 0);

  const totalTransactions =
    Number(stats.sales || 0) +
    Number(stats.repairs || 0);

  const averageSale =
    stats.sales > 0
      ? stats.salesTotal / stats.sales
      : 0;

  const conversionRate =
    stats.visitors > 0
      ? (totalTransactions / stats.visitors) * 100
      : 0;

  /* =====================================================
     SALES BY USER
  ===================================================== */

  /*
   * Bonusi NUK llogaritet më me një përqindje fikse në Dashboard.
   *
   * Backend-i duhet ta llogarisë për çdo produkt sipas tabelës Products:
   *
   * fitimi = price - purchase_price
   * bonusProdukt = fitimi * (bonus_share_percent / 100)
   *
   * dhe të kthejë totalin e bonusit për çdo shitës te:
   * salesByUser[].bonus
   */
  const salesByUserData = stats.salesByUser.map((item) => ({
    id: item.seller_id,
    name:
      item.seller_name ||
      item.username ||
      "Përdorues i panjohur",
    role: item.seller_role || "user",
    sales: Number(item.sales_count || 0),
    quantity: Number(item.quantity_sold || 0),
    total: Number(item.total || 0),
    bonus: Number(
      item.bonus ??
      item.total_bonus ??
      item.sales_bonus ??
      0
    ),
  }));

  const totalSalesBonuses = salesByUserData.reduce(
    (sum, item) => sum + Number(item.bonus || 0),
    0
  );

  /* =====================================================
     REPAIRS BY USER
  ===================================================== */

  const repairsByUserData = (stats?.repairsByUser || []).map((item) => ({
    id:
      item.completed_by_user_id ??
      item.user_id ??
      item.technician_id ??
      item.id,

    name:
      item.completed_by_user_name ||
      item.user_name ||
      item.technician_name ||
      item.username ||
      "Përdorues i panjohur",

    repairs: Number(
      item.repairs_count ??
      item.repair_count ??
      item.count ??
      0
    ),

    total: Number(
      item.total ??
      item.repairs_total ??
      item.revenue ??
      0
    ),

    repairCost: Number(
      item.repair_cost_total ??
      item.total_repair_cost ??
      0
    ),

    profit: Number(
      item.profit ??
      item.technician_profit ??
      item.user_profit ??
      0
    ),
  }));

  /* =====================================================
     COMBINED REVENUE TIMELINE
  ===================================================== */

  const revenueTimeline = useMemo(() => {
    const map = new Map();

    // 1. Krijojmë çdo ditë të range-it me vlerë 0
    if (appliedStartDate && appliedEndDate) {
      const cursor = new Date(`${appliedStartDate}T12:00:00`);
      const lastDate = new Date(`${appliedEndDate}T12:00:00`);

      while (cursor <= lastDate) {
        const year = cursor.getFullYear();
        const month = String(cursor.getMonth() + 1).padStart(2, "0");
        const day = String(cursor.getDate()).padStart(2, "0");

        const dateKey = `${year}-${month}-${day}`;

        map.set(dateKey, {
          date: dateKey,
          label: formatDateDMY(dateKey),
          sales: 0,
          repairs: 0,
        });

        cursor.setDate(cursor.getDate() + 1);
      }
    }

    // 2. Fusim shitjet reale
    stats.salesTimeline
      .filter((item) =>
        isDateInsideRange(
          item.date || item.day,
          appliedStartDate,
          appliedEndDate
        )
      )
      .forEach((item) => {
        const dateKey = normalizeDateValue(item.date || item.day);

        if (!dateKey) return;

        const current = map.get(dateKey) || {
          date: dateKey,
          label: formatDateDMY(dateKey),
          sales: 0,
          repairs: 0,
        };

        current.sales = Number(item.total || 0);

        map.set(dateKey, current);
      });

    // 3. Fusim serviset reale
    stats.repairsTimeline
      .filter((item) =>
        isDateInsideRange(
          item.date || item.day,
          appliedStartDate,
          appliedEndDate
        )
      )
      .forEach((item) => {
        const dateKey = normalizeDateValue(item.date || item.day);

        if (!dateKey) return;

        const current = map.get(dateKey) || {
          date: dateKey,
          label: formatDateDMY(dateKey),
          sales: 0,
          repairs: 0,
        };

        current.repairs = Number(
          item.total ||
          item.revenue ||
          item.repairsTotal ||
          0
        );

        map.set(dateKey, current);
      });

    // 4. Rendisim nga data më e vjetër te më e reja
    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [
    stats.salesTimeline,
    stats.repairsTimeline,
    appliedStartDate,
    appliedEndDate,
  ]);

  // Aggregate the chart by day, week or month so long date ranges stay readable.
  const revenueChart = useMemo(() => {
    const days = revenueTimeline.length;
    const mode = days > 62 ? "month" : days > 14 ? "week" : "day";
    const grouped = new Map();

    revenueTimeline.forEach((item) => {
      const date = new Date(`${item.date}T12:00:00`);
      let key = item.date;
      let label = item.label;

      if (mode === "month") {
        key = item.date.slice(0, 7);
        label = new Intl.DateTimeFormat("sq-AL", {
          month: "short", year: "numeric",
        }).format(date);
      } else if (mode === "week") {
        const monday = new Date(date);
        const weekday = (monday.getDay() + 6) % 7;
        monday.setDate(monday.getDate() - weekday);
        const y = monday.getFullYear();
        const m = String(monday.getMonth() + 1).padStart(2, "0");
        const d = String(monday.getDate()).padStart(2, "0");
        key = `${y}-${m}-${d}`;
        label = `Java ${d}/${m}`;
      }

      if (!grouped.has(key)) {
        grouped.set(key, { date: key, label, sales: 0, repairs: 0 });
      }
      const row = grouped.get(key);
      row.sales += Number(item.sales || 0);
      row.repairs += Number(item.repairs || 0);
    });

    return { mode, data: Array.from(grouped.values()) };
  }, [revenueTimeline]);

  /* =====================================================
     VISITORS TIMELINE
  ===================================================== */

  const visitorsTimelineData = useMemo(() => {
    return stats.visitorsTimeline
      .filter((item) =>
        isDateInsideRange(
          item.date || item.day,
          appliedStartDate,
          appliedEndDate
        )
      )
      .map((item) => {
        const dateKey = normalizeDateValue(item.date || item.day);

        return {
          date: dateKey,
          label: dateKey ? formatDateDMY(dateKey) : item.label || "",
          visitors: Number(
            item.visitors ||
            item.count ||
            item.total ||
            0
          ),
        };
      })
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [stats.visitorsTimeline, appliedStartDate, appliedEndDate]);

  /* =====================================================
     KPI CARDS
  ===================================================== */

  const cards = [
    {
      label: "Gjendje ne dyqan",
      value: stats.products,
      icon: <Package size={20} />,
      color: "from-blue-500 to-blue-600",
      route: "/admin/device-purchases",
    },
    {
      label: "Katalogu i Webit",
      value: stats.productTypes,
      icon: <Boxes size={20} />,
      color: "from-sky-500 to-sky-600",
      route: "/admin/products",
    },
    {
      label: "Pjesë",
      value: stats.parts,
      icon: <Wrench size={20} />,
      color: "from-emerald-500 to-emerald-600",
      route: "/admin/parts",
    },
    {
      label: "Shitje",
      value: stats.sales,
      icon: <ShoppingCart size={20} />,
      color: "from-indigo-500 to-indigo-600",
      route: "/admin/sales",
    },
    {
      label: "Të ardhura nga shitjet",
      value: `${formatMoney(stats.salesTotal)} ALL`,
      icon: <BadgeDollarSign size={20} />,
      color: "from-cyan-500 to-cyan-600",
      compact: true,
      route: "/admin/sales",
    },
    {
      label: "Shitje nga Webi ",
      value: stats.orders,
      icon: <ShoppingCart size={20} />,
      color: "from-orange-500 to-orange-600",
      route: "/admin/orders",
    },
    {
      label: "Servise",
      value: stats.repairs,
      icon: <RefreshCcw size={20} />,
      color: "from-purple-500 to-purple-600",
      route: "/admin/repairs",
    },
    {
      label: "Të ardhura nga serviset",
      value: `${formatMoney(stats.repairsTotal)} ALL`,
      icon: <BadgeDollarSign size={20} />,
      color: "from-fuchsia-500 to-fuchsia-600",
      compact: true,
      route: "/admin/repairs",
    },
    {
      label: "Vizitorë në dyqan",
      value: stats.visitors,
      icon: <Eye size={20} />,
      color: "from-blue-400 to-blue-500",
      route: "/admin/analysis",
    },
    {
      label: "Vizitorë nga webi",
      value: stats.webVisitors,
      icon: <Users size={20} />,
      color: "from-violet-500 to-violet-600",
      route: "/admin/analysis",
    },
    {
      label: "Përdorues",
      value: stats.users,
      icon: <Users size={20} />,
      color: "from-red-500 to-rose-500",
      route: "/admin/users",
    },
    {
      label: "Të ardhura totale",
      value: `${formatMoney(totalRevenue)} ALL`,
      icon: <ChartNoAxesCombined size={20} />,
      color: "from-amber-400 to-orange-500",
      compact: true,
      route: "/admin/analysis",
    },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="
          fixed left-4 top-4 z-30
          flex h-11 w-11
          items-center justify-center
          rounded-xl bg-blue-700
          text-white shadow-lg
          lg:hidden
        "
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>
      <main
        className="
            min-h-screen
            min-w-0
            w-full
            ml-0
            overflow-x-hidden
            px-2
            pb-8
            pt-20

            sm:w-full
            sm:ml-0
            sm:px-4

            lg:ml-64
            lg:w-[calc(100%-16rem)]
            lg:px-7 lg:py-8
          "
      >
        {/* ================= HEADER ================= */}

        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Paneli i administrimit
            </span>

            <h1 className="mt-1 text-3xl font-bold text-slate-900">
              Admin Dashboard
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              Mirë se erdhe,{" "}
              <strong className="text-slate-700">
                {currentUser?.username ||
                  currentUser?.name ||
                  "Administrator"}
              </strong>
            </p>
          </div>

          <div className="relative w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setShowDateRange((value) => !value)}
              className="
                flex min-h-[64px] w-full items-center justify-between gap-4
                rounded-2xl border border-slate-200
                bg-white px-5 py-3
                text-left shadow-sm transition
                hover:border-blue-300 hover:shadow-md
                sm:min-w-[300px]
              "
            >
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <CalendarDays size={22} />
                </div>

                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Data e sotme
                  </p>
                  <p className="mt-0.5 text-lg font-extrabold text-slate-900">
                    {formatDateDMY(today)}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-[11px] font-semibold text-slate-400">
                  Range aktiv
                </p>
                <p className="mt-0.5 break-words text-xs font-bold text-blue-700">
                  {formatDateDMY(appliedStartDate)} - {formatDateDMY(appliedEndDate)}
                </p>
              </div>
            </button>

            {showDateRange && (
              <div
                className="
                  absolute right-0 top-[74px] z-40
                  w-full min-w-[320px]
                  rounded-2xl border border-slate-200
                  bg-white p-4 shadow-xl
                  sm:w-[430px]
                "
              >
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-extrabold text-slate-900">
                      Zgjidh periudhën
                    </p>
                    <p className="text-xs text-slate-400">
                      Formati: ditë / muaj / vit
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowDateRange(false)}
                    className="rounded-lg px-2 py-1 text-sm font-bold text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Mbyll"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-500">
                      Nga
                    </label>

                    <label
                      className="
                        relative flex h-12 w-full cursor-pointer items-center
                        rounded-xl border border-slate-200 bg-white px-3
                        text-base font-bold text-slate-700
                        transition hover:border-blue-300
                        focus-within:border-blue-500
                        focus-within:ring-4 focus-within:ring-blue-100
                      "
                    >
                      <span>{formatDateDMY(startDate)}</span>

                      <CalendarDays
                        size={18}
                        className="ml-auto text-blue-600"
                      />

                      <input
                        type="date"
                        value={startDate}
                        max={endDate || today}
                        onChange={(event) => setStartDate(event.target.value)}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        aria-label="Data nga"
                      />
                    </label>

                    <p className="mt-1 text-[11px] font-semibold text-slate-400">
                      ditë / muaj / vit
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-500">
                      Deri
                    </label>

                    <label
                      className="
                        relative flex h-12 w-full cursor-pointer items-center
                        rounded-xl border border-slate-200 bg-white px-3
                        text-base font-bold text-slate-700
                        transition hover:border-blue-300
                        focus-within:border-blue-500
                        focus-within:ring-4 focus-within:ring-blue-100
                      "
                    >
                      <span>{formatDateDMY(endDate)}</span>

                      <CalendarDays
                        size={18}
                        className="ml-auto text-blue-600"
                      />

                      <input
                        type="date"
                        value={endDate}
                        min={startDate || undefined}
                        max={today}
                        onChange={(event) => setEndDate(event.target.value)}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        aria-label="Data deri"
                      />
                    </label>

                    <p className="mt-1 text-[11px] font-semibold text-slate-400">
                      ditë / muaj / vit
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={loading}
                    className="
                      flex h-12 flex-1 items-center justify-center gap-2
                      rounded-xl bg-blue-700 px-5
                      text-sm font-bold text-white
                      shadow-sm transition
                      hover:bg-blue-800
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                    "
                  >
                    <SearchCheck size={18} />
                    Kërko
                  </button>

                  <button
                    type="button"
                    onClick={() => setRefreshKey((value) => value + 1)}
                    disabled={loading}
                    className="
                      flex h-12 w-12 items-center justify-center
                      rounded-xl border border-slate-200
                      bg-white text-slate-600
                      shadow-sm transition hover:bg-slate-50
                      disabled:opacity-60
                    "
                    title="Rifresko"
                    aria-label="Rifresko"
                  >
                    <RefreshCcw
                      size={18}
                      className={loading ? "animate-spin" : ""}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ================= STATUS ================= */}

        {loading && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            <RefreshCcw
              size={17}
              className="animate-spin"
            />
            Duke ngarkuar statistikat...
          </div>
        )}

        {error && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}


        {/* ================= KPI CARDS ================= */}

        <div
          className="
            mb-7 grid grid-cols-2 gap-3
            sm:grid-cols-3
            lg:grid-cols-3
            xl:grid-cols-4
            2xl:grid-cols-6
          "
        >
          {cards.map((card) => (
            <button
              key={card.label}
              type="button"
              onClick={() => card.route && navigate(card.route)}
              className={`
                min-h-[142px] min-w-0 overflow-hidden
                rounded-2xl
                bg-gradient-to-br
                ${card.color}
                p-4 text-left text-white
                shadow-[0_6px_18px_rgba(15,23,42,0.10)]
                ring-1 ring-white/10
                transition
                hover:-translate-y-0.5
                hover:shadow-xl
                focus:outline-none
                focus:ring-4
                focus:ring-blue-200
                active:translate-y-0
                ${card.route ? "cursor-pointer" : "cursor-default"}
              `}
              title={card.route ? `Hap ${card.label}` : card.label}
            >
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="shrink-0 rounded-xl bg-white/20 p-2.5 ring-1 ring-white/20">
                  {card.icon}
                </div>

                <span
                  className={
                    card.compact
                      ? "min-w-0 max-w-full break-words text-right text-[clamp(0.95rem,1.35vw,1.3rem)] font-extrabold leading-snug tabular-nums"
                      : "min-w-0 break-words text-2xl font-extrabold tabular-nums"
                  }
                >
                  {card.value}
                </span>
              </div>

              <div className="mt-5 flex min-w-0 items-end justify-between gap-2">
                <p className="min-w-0 text-[12px] font-semibold leading-snug text-white/95">
                  {card.label}
                </p>

                {card.route && (
                  <span className="shrink-0 text-sm font-bold text-white/80">→</span>
                )}
              </div>

              {card.subValue && (
                <p className="mt-1 text-[11px] font-semibold text-white/80">
                  {card.subValue}
                </p>
              )}
            </button>
          ))}
        </div>


        {/* ================= FINANCIAL BALANCE ================= */}
        <section className="mb-7 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">MasterPhone · Financa</p>
              <h2 className="mt-1 text-xl font-extrabold text-slate-900">Bilanci financiar</h2>
              <p className="mt-1 text-xs text-slate-500">{formatDateDMY(appliedStartDate)} — {formatDateDMY(appliedEndDate)}</p>
            </div>
            <div className="flex w-full rounded-xl bg-slate-100 p-1 sm:w-auto">
              {[["day", "Ditor"], ["month", "Mujor"], ["year", "Vjetor"]].map(([key, label]) => (
                <button key={key} type="button" onClick={() => applyBalancePeriod(key)}
                  className={`flex-1 rounded-lg px-4 py-2 text-sm font-bold transition ${balancePeriod === key ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-5 p-4 sm:p-5">
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
              {[
                { label: "Xhiro totale", amount: totalRevenue, icon: <Wallet size={18} />, color: "text-blue-700", enabled: true },
                { label: "Dalje të njohura", amount: smartTotals?.knownCashOut, icon: <ReceiptText size={18} />, color: "text-rose-700", enabled: smartTotals?.knownCashOut != null },
                { label: "Tërheqje personale", amount: smartWithdrawals?.total, icon: <ArrowDownToLine size={18} />, color: "text-amber-700", enabled: smartWithdrawals?.total != null },
                { label: "Fitimi i mbetur", amount: remainingProfit, icon: <CircleDollarSign size={18} />, color: Number(remainingProfit) < 0 ? "text-rose-700" : "text-emerald-700", enabled: remainingProfit != null },
              ].map((item) => (
                <div key={item.label} className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                  <div className={`flex items-start gap-2 text-xs font-semibold ${item.color}`}>{item.icon}<span>{item.label}</span></div>
                  <p className={`mt-3 break-words text-lg font-extrabold tabular-nums sm:text-xl ${item.label === "Fitimi i mbetur" ? (Number(item.amount) < 0 ? "text-rose-700" : "text-emerald-700") : "text-slate-900"}`}>
                    {item.enabled ? `${formatMoney(item.amount)} ALL` : "—"}
                  </p>
                </div>
              ))}
            </div>

            {/* Intelligent balance is computed by the backend per transaction. */}
            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-extrabold text-slate-900">Bilanci inteligjent · fitimi real</h3>
                <span className="text-xs font-semibold text-blue-700">Sipas kostos për çdo produkt dhe servis</span>
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[
                  { label: "Fitimi nga shitjet", amount: smartSales?.profit },
                  { label: "Fitimi i dyqanit nga serviset", amount: smartRepairs?.profit },
                  { label: "Pjesa e Bekimit nga serviset", amount: smartRepairs?.bekimShare },
                  { label: "Fitimi bruto", amount: smartTotals?.grossProfit },
                  { label: "Fitimi pas shpenzimeve operative", amount: smartTotals?.operatingResult },
                  { label: "Shpenzimet e dyqanit", amount: smartTotals?.operatingExpenses },
                  { label: "Pagesat e furnitorëve", amount: supplierDeduction },
                  { label: "Blerje pajisjesh", amount: deviceDeduction },
                  { label: "Tërheqjet personale", amount: smartWithdrawals?.total },
                  { label: "FITIMI I MBETUR", amount: remainingProfit },
                ].map((item) => (
                  <div key={item.label} className="min-w-0 rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-xs font-semibold text-slate-500">{item.label}</p>
                    <p className={`mt-2 break-words text-lg font-extrabold tabular-nums ${item.amount != null && Number(item.amount) < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {smartMoney(item.amount)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {[
                  { label: "Tërheqje personale", amount: smartWithdrawals?.total },
                  { label: "Dalje të njohura të arkës", amount: smartTotals?.knownCashOut },
                  { label: "Lëvizja neto e njohur e arkës", amount: smartTotals?.knownCashMovement },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                    <p className="text-xs text-slate-500">{item.label}</p>
                    <p className="mt-1 font-bold tabular-nums text-slate-900">{smartMoney(item.amount)}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">Fitimi i mbetur = fitimi nga shitjet + fitimi i dyqanit nga serviset − shpenzimet e dyqanit − pagesat e furnitorëve − blerjet e pajisjeve − tërheqjet personale. Tërheqjet e regjistruara te shpenzimet ndahen veçmas që të mos zbriten dy herë.</p>
              {!smartBalance && <p className="mt-3 text-sm font-semibold text-amber-700">API nuk ka kthyer intelligentBalance. Fitimi real dhe tërheqjet e verifikuara nuk mund të shfaqen. Kontrollo në Network përgjigjen GET /api/stats dhe sigurohu që backend-i në portën që përdor frontend-i ka stats.js të ri.</p>}
              {smartAlerts.length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-bold text-amber-900">Sinjalizime për verifikim</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-900">
                    {smartAlerts.map((alert, index) => <li key={`${index}-${String(alert)}`}>{String(alert)}</li>)}
                  </ul>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-500">
              Fitimi i mbetur llogaritet duke zbritur shpenzimet operative dhe tërheqjet personale nga fitimi i shitjeve dhe pjesa e dyqanit nga serviset. Blerjet e pajisjeve dhe pagesat e furnitorëve zbriten si dalje sipas rregullit të bilancit të kërkuar; kjo nuk është e njëjta gjë me fitimin kontabël.
            </p>

            {expensesError && (
              <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {expensesError} Kontrollo lidhjen me API-në e shpenzimeve dhe autentikimin.
              </p>
            )}

            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
                <h3 className="font-bold text-slate-900">Përmbledhja e lëvizjeve</h3>
              </div>
              <div className="divide-y divide-slate-100 px-4">
                {[
                  { label: "Shitje", amount: stats.salesTotal },
                  { label: "Servise", amount: stats.repairsTotal },
                  { label: "Xhiro totale", amount: totalRevenue, bold: true },
                  { label: "Shpenzime të dyqanit", amount: financial.operatingTotal, requiresExpenses: true },
                  { label: "Tërheqje nga shpenzimet", amount: financial.personalFromExpenses, requiresExpenses: true },
                   { label: "Rroga dhe paradhënie", amount: financial.payrollPayments, requiresExpenses: true },
                  { label: "Blerje pajisjesh", amount: financial.devicePurchases, requiresExpenses: true },
                   { label: "Pagesa furnitorësh", amount: financial.supplierPayments, requiresExpenses: true },
                  { label: "Dalje totale të regjistruara", amount: -financial.total, bold: true, requiresExpenses: true },
                  { label: "Fitimi / Humbja (sipas lëvizjeve)", amount: stats.cashResult, bold: true, requiresExpenses: true },
                ].map((item) => (
                  <div key={item.label} className={`flex items-center justify-between gap-3 py-3 text-sm ${item.bold ? "font-extrabold text-slate-900" : "text-slate-600"}`}>
                    <span>{item.label}</span>
                    <span className={`shrink-0 tabular-nums ${Number(item.amount) < 0 ? "text-rose-700" : ""}`}>{item.requiresExpenses && !expensesReady ? "—" : `${formatMoney(item.amount)} ALL`}</span>
                  </div>
                ))}
              </div>
            </div>

             <div className="rounded-xl border border-slate-200 p-4">
               <h3 className="font-bold text-slate-900">Tërheqjet personale · Leard, Bekim, Marin, Nolti</h3>
               <p className="mt-1 text-xs text-slate-500">Të dhëna nga shpenzimet historike dhe pagesat e regjistruara në payroll.</p>
               <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                 {["Leard", "Bekim", "Marin", "Nolti"].map((name) => {
                   const person = withdrawalFor(name);
                   return (
                     <div key={name} className="rounded-xl bg-slate-50 p-3">
                       <p className="font-semibold text-slate-700">{name}</p>
                       <p className="mt-2 text-lg font-extrabold text-amber-700">{person ? smartMoney(person.amount) : "—"}</p>
                       <p className="text-xs text-slate-500">{person ? person.count : "—"} tërheqje{person && !person.verified ? " (nga raporti i vjetër, për verifikim)" : ""}</p>
                     </div>
                   );
                 })}
               </div>
               <p className="mt-3 text-xs text-slate-500">Nëse një tërheqje nuk shfaqet, verifiko emrin dhe datën në databazë; zero nuk konfirmon mungesën e pagesës.</p>
             </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-bold text-slate-900">Shpenzimet sipas kategorisë</h3>
                <p className="mt-1 text-xs text-slate-500">Totali i çdo kategorie, pa përfshirë tërheqjet personale</p>
                <div className="mt-3 divide-y divide-slate-100">
                  {!expensesReady ? <p className="py-3 text-sm text-slate-500">Të dhënat nuk janë të disponueshme.</p> :
                    financial.categories.length ? financial.categories.map((category) => (
                      <div key={category.name} className="flex justify-between gap-3 py-2.5 text-sm">
                        <span className="break-words text-slate-700">{category.name}</span>
                        <span className="shrink-0 font-bold tabular-nums text-slate-900">{formatMoney(category.amount)} ALL</span>
                      </div>
                    )) : <p className="py-3 text-sm text-slate-500">Nuk ka shpenzime të dyqanit.</p>}
                </div>
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-3 text-sm font-extrabold">
                  <span>Totali</span><span>{expensesReady ? `${formatMoney(financial.categories.reduce((sum, item) => sum + item.amount, 0))} ALL` : "—"}</span>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-bold text-slate-900">Tërheqjet sipas përdoruesit</h3>
                <p className="mt-1 text-xs text-slate-500">Nga Shpenzimet (emri në përshkrim) dhe nga Rrogat</p>
                <div className="mt-3 divide-y divide-slate-100">
                  {!expensesReady ? <p className="py-3 text-sm text-slate-500">Të dhënat nuk janë të disponueshme.</p> :
                    financial.personal.length ? financial.personal.map((person) => (
                      <div key={person.id} className="flex justify-between gap-3 py-2.5 text-sm">
                        <span className="min-w-0 text-slate-700">{person.name}<span className="ml-2 text-xs text-slate-400">({person.count} pagesa)</span></span>
                        <span className="shrink-0 font-bold tabular-nums text-slate-900">{formatMoney(person.amount)} ALL</span>
                      </div>
                    )) : <p className="py-3 text-sm text-slate-500">Nuk ka tërheqje personale.</p>}
                </div>
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-3 text-sm font-extrabold">
                  <span>Totali</span><span>{expensesReady ? `${formatMoney(financial.personalTotal)} ALL` : "—"}</span>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ================= MAIN ANALYTICS ================= */}

        <div className="mb-6 grid min-w-0 gap-4 xl:grid-cols-12">
          {/* REVENUE */}
          <section className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm xl:col-span-8">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="font-bold text-slate-900">Ecuria e të ardhurave</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Shitjet dhe serviset sipas {revenueChart.mode === "month" ? "muajve" : revenueChart.mode === "week" ? "javëve" : "ditëve"}.
                </p>
              </div>
              <span className="rounded-lg bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                {formatMoney(totalRevenue)} ALL gjithsej
              </span>
            </div>

            {revenueChart.data.length === 0 ? (
              <div className="flex h-[320px] items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400">
                Nuk ka të dhëna për këtë periudhë.
              </div>
            ) : (
              <div className="w-full" style={{ height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={revenueChart.data}
                    barCategoryGap="22%"
                    barGap={3}
                    margin={{ top: 30, right: 12, left: 4, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={false}
                      interval={revenueChart.data.length > 15 ? "preserveStartEnd" : 0}
                      minTickGap={12}
                    />
                    <YAxis
                      tickFormatter={(value) => new Intl.NumberFormat("sq-AL", { notation: "compact", maximumFractionDigits: 1 }).format(value)}
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={false}
                      width={48}
                    />
                    <Tooltip
                      formatter={(value, name) => [`${formatMoney(value)} ALL`, name]}
                      contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }}
                    />
                    <Legend verticalAlign="bottom" height={30} />
                    <Bar dataKey="sales" name="Shitje" fill="#2563eb" radius={[5, 5, 0, 0]} maxBarSize={52} minPointSize={0}>
                      {revenueChart.data.length <= 12 && (
                        <LabelList dataKey="sales" position="top" fontSize={10} fill="#1d4ed8"
                          formatter={(value) => Number(value) > 0 ? new Intl.NumberFormat("sq-AL", { notation: "compact", maximumFractionDigits: 1 }).format(value) : ""} />
                      )}
                    </Bar>
                    <Bar dataKey="repairs" name="Servise" fill="#16a34a" radius={[5, 5, 0, 0]} maxBarSize={52} minPointSize={0}>
                      {revenueChart.data.length <= 12 && (
                        <LabelList dataKey="repairs" position="top" fontSize={10} fill="#15803d"
                          formatter={(value) => Number(value) > 0 ? new Intl.NumberFormat("sq-AL", { notation: "compact", maximumFractionDigits: 1 }).format(value) : ""} />
                      )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          {/* VISITORS */}
          <section className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.035)] xl:col-span-4 min-w-0">
            <div className="mb-4">
              <h2 className="font-bold text-slate-900">
                Vizitorët
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Numri i vizitorëve gjatë periudhës së zgjedhur.
              </p>
            </div>

            {visitorsTimelineData.length === 0 ? (
              <div className="flex h-[300px] flex-col items-center justify-center rounded-xl bg-slate-50 text-center">
                <Eye
                  size={28}
                  className="mb-2 text-slate-300"
                />
                <p className="text-sm text-slate-400">
                  Grafiku do të shfaqet sapo backend-i të kthejë
                  <strong> visitorsTimeline</strong>.
                </p>
                <p className="mt-2 text-3xl font-bold text-blue-600">
                  {stats.visitors}
                </p>
                <span className="text-xs text-slate-400">
                  vizitorë gjithsej
                </span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={visitorsTimelineData} margin={{ top: 16, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" minTickGap={18} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip formatter={(value) => [value, "Vizitorë"]} />
                  <Bar dataKey="visitors" name="Vizitorë" fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>

         
        </div>

        {/* ================= TOP PRODUCTS SUMMARY ================= */}

        <section className="mb-6 min-w-0 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.035)]">
          <h2 className="font-bold text-slate-900">
            Përmbledhje e produkteve
          </h2>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl bg-emerald-50 p-4">
              <p className="text-xs font-medium text-slate-500">
                Produkti më i shitur
              </p>
              <p className="mt-2 break-words text-base font-extrabold text-emerald-700">
                {stats.topSoldProduct?.product_name || "—"}
              </p>
              <p className="mt-1 text-xs font-semibold text-emerald-600">
                {stats.topSoldProduct
                  ? `${Number(stats.topSoldProduct.quantity_sold || 0)} copë`
                  : "Nuk ka të dhëna"}
              </p>
            </div>

            <div className="rounded-xl bg-fuchsia-50 p-4">
              <p className="text-xs font-medium text-slate-500">
                Produkti më i klikuar
              </p>
              <p className="mt-2 break-words text-base font-extrabold text-fuchsia-700">
                {stats.topClickedProduct?.product_name || "—"}
              </p>
              <p className="mt-1 text-xs font-semibold text-fuchsia-600">
                {stats.topClickedProduct
                  ? `${Number(stats.topClickedProduct.clicks || 0)} klikime`
                  : "Nuk ka të dhëna"}
              </p>
            </div>

            <div className="rounded-xl bg-cyan-50 p-4">
              <p className="text-xs font-medium text-slate-500">
                Produkti më i kërkuar
              </p>
              <p className="mt-2 break-words text-base font-extrabold text-cyan-700">
                {stats.topSearchedProduct?.product_name ||
                  stats.topSearchTerm?.search_term ||
                  "—"}
              </p>
              <p className="mt-1 text-xs font-semibold text-cyan-600">
                {stats.topSearchedProduct || stats.topSearchTerm
                  ? `${Number(
                    stats.topSearchedProduct?.searches ||
                    stats.topSearchTerm?.searches ||
                    0
                  )} kërkime`
                  : "Nuk ka të dhëna"}
              </p>
            </div>
          </div>
        </section>

        {/* ================= USER ANALYTICS ================= */}

        <div className="grid gap-5 xl:grid-cols-2">
          {/* SALES BY USER */}
          <section className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.035)]">
            <div className="mb-5">
              <h2 className="font-bold text-slate-900">
                Shitjet sipas përdoruesit
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Shitjet, të ardhurat dhe bonusi sipas fitimit të produkteve.
              </p>
            </div>

            {salesByUserData.length === 0 ? (
              <div className="flex h-[260px] items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400">
                Nuk ka shitje të regjistruara.
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={salesByUserData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />

                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                    />

                    <YAxis allowDecimals={false} />

                    <Tooltip
                      formatter={(value) => [
                        value,
                        "Shitje",
                      ]}
                    />

                    <Bar
                      dataKey="sales"
                      name="Shitje"
                      fill="#2563eb"
                      radius={[7, 7, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-4 space-y-2">
                  {salesByUserData
                    .slice(0, 5)
                    .map((item, index) => (
                      <div
                        key={
                          item.id ??
                          `${item.name}-${index}`
                        }
                        className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-semibold text-slate-800">
                            {item.name}
                          </p>
                          <p className="text-xs text-slate-400">
                            {item.sales} shitje ·{" "}
                            {item.quantity} artikuj
                          </p>
                        </div>

                        <div className="text-right">
                          <span className="block text-sm font-bold text-blue-600">
                            {formatMoney(item.total)} ALL
                          </span>
                          <span className="mt-1 block text-xs font-semibold text-pink-600">
                            Bonus: {formatMoney(item.bonus)} ALL
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </section>

          {/* REPAIRS BY USER */}
          <section className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.035)]">
            <div className="mb-5">
              <h2 className="font-bold text-slate-900">
                Serviset sipas përdoruesit
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Serviset me status gati, sipas përdoruesit që i përfundoi.
              </p>
            </div>

            {repairsByUserData.length === 0 ? (
              <div className="flex h-[260px] items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-400">
                Nuk ka të dhëna servisi sipas përdoruesit.
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={repairsByUserData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />

                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                    />

                    <YAxis allowDecimals={false} />

                    <Tooltip />

                    <Bar
                      dataKey="repairs"
                      name="Servise"
                      fill="#10b981"
                      radius={[7, 7, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-4 space-y-2">
                  {repairsByUserData
                    .slice(0, 5)
                    .map((item, index) => (
                      <div
                        key={
                          item.id ??
                          `${item.name}-${index}`
                        }
                        className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-semibold text-slate-800">
                            {item.name}
                          </p>
                          <p className="text-xs text-slate-400">
                            {item.repairs} servise
                          </p>
                        </div>

                        <div className="text-right">
                          <span className="block text-sm font-bold text-emerald-600">
                            {formatMoney(item.total)} ALL
                          </span>

                          <span className="mt-1 block text-xs font-bold text-violet-600">
                            Fitimi: {formatMoney(item.profit)} ALL
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </section>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Të gjitha të dhënat e dashboard-it filtrohen strikt sipas periudhës së zgjedhur.
        </p>
      </main>
    </div>
  );
};

export default AdminDashboard;